/**
 * MeetingLinkEditor Component
 * 
 * Allows teachers to update the meeting link for synchronous courses.
 * Shows current link, last update time, and link history (last 5).
 * Validates URL format before submission.
 * 
 * Requirements: 2.1, 2.3, 2.6
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import { 
  Link2, 
  Clock, 
  History, 
  Check, 
  AlertCircle, 
  Loader2,
  ExternalLink,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

/**
 * Payment Service base URL from environment variable
 */
const PAYMENT_API_URL = import.meta.env.VITE_PAYMENT_API_URL || 'http://localhost:3001';

/**
 * URL validation regex - must start with http:// or https://
 * Requirements: 2.3
 */
const URL_REGEX = /^https?:\/\/.+/i;

/**
 * Validates if a string is a valid meeting link URL
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid
 */
function isValidUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return URL_REGEX.test(url.trim());
}

/**
 * Format timestamp for display in Spanish locale
 * @param {string} isoString - ISO timestamp
 * @returns {string} Formatted date string
 */
function formatTimestamp(isoString) {
  if (!isoString) return null;
  try {
    const date = new Date(isoString);
    return date.toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return null;
  }
}

/**
 * MeetingLinkEditor Component
 * 
 * @param {number} courseId - Course ID to manage
 * @param {string} courseName - Course name for display
 * @param {string} currentLink - Current meeting link URL
 * @param {string} lastUpdatedAt - ISO timestamp of last update
 * @param {string} token - Teacher JWT token for API calls
 * @param {Function} onLinkUpdated - Callback when link is successfully updated
 * @param {string} className - Additional CSS classes
 */
export default function MeetingLinkEditor({
  courseId,
  courseName = '',
  currentLink = '',
  lastUpdatedAt = null,
  token,
  onLinkUpdated = null,
  className = '',
}) {
  // Form state
  const [linkInput, setLinkInput] = useState(currentLink || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // History state
  const [history, setHistory] = useState([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // Update input when currentLink prop changes
  useEffect(() => {
    setLinkInput(currentLink || '');
  }, [currentLink]);

  /**
   * Fetch meeting link history from API
   * Requirements: 2.6
   */
  const fetchHistory = async () => {
    if (!courseId || !token) return;
    
    setIsLoadingHistory(true);
    setHistoryError(null);
    
    try {
      const response = await fetch(
        `${PAYMENT_API_URL}/api/teacher/courses/${courseId}/meeting-link-history`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error al cargar historial');
      }
      
      setHistory(data.history || []);
    } catch (err) {
      setHistoryError(err.message);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  /**
   * Toggle history panel and fetch if opening
   */
  const toggleHistory = () => {
    const newState = !isHistoryOpen;
    setIsHistoryOpen(newState);
    
    if (newState && history.length === 0) {
      fetchHistory();
    }
  };

  /**
   * Handle form submission
   * Requirements: 2.1, 2.3
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setError(null);
    setSuccess(null);
    
    const trimmedLink = linkInput.trim();
    
    // Validate URL format
    if (!isValidUrl(trimmedLink)) {
      setError('El enlace debe ser una URL válida (http:// o https://)');
      return;
    }
    
    // Check if link is the same as current
    if (trimmedLink === currentLink) {
      setError('El enlace es el mismo que el actual');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(
        `${PAYMENT_API_URL}/api/teacher/courses/${courseId}/meeting-link`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ meetingLink: trimmedLink }),
        }
      );
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error al actualizar el enlace');
      }
      
      // Show success message
      const notifMsg = data.notificationsSent > 0 
        ? ` Se notificó a ${data.notificationsSent} estudiante(s).`
        : '';
      setSuccess(`Enlace actualizado correctamente.${notifMsg}`);
      
      // Refresh history if open
      if (isHistoryOpen) {
        fetchHistory();
      }
      
      // Call callback
      if (onLinkUpdated) {
        onLinkUpdated({
          meetingLink: data.meetingLink,
          updatedAt: data.updatedAt,
          notificationsSent: data.notificationsSent,
        });
      }
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
      
    } catch (err) {
      setError(err.message || 'Error al actualizar el enlace');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle input change
   */
  const handleInputChange = (e) => {
    setLinkInput(e.target.value);
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const formattedLastUpdate = formatTimestamp(lastUpdatedAt);
  const hasCurrentLink = currentLink && currentLink.trim() !== '';

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Current Link Display */}
      {hasCurrentLink && (
        <motion.div
          className="p-4 rounded-xl bg-white/5 border border-white/10"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white/50 mb-1">Enlace actual</p>
              <a
                href={currentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-cyan-400 hover:text-cyan-300 
                           flex items-center gap-1 truncate transition-colors"
              >
                <span className="truncate">{currentLink}</span>
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            </div>
            {formattedLastUpdate && (
              <div className="flex items-center gap-1 text-xs text-white/40 flex-shrink-0">
                <Clock className="w-3 h-3" />
                <span>{formattedLastUpdate}</span>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Update Form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Input Field */}
        <div>
          <label 
            htmlFor={`meeting-link-${courseId}`}
            className="block text-sm font-medium text-white/80 mb-2"
          >
            {hasCurrentLink ? 'Actualizar enlace de reunión' : 'Agregar enlace de reunión'}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Link2 className="w-4 h-4 text-slate-500" />
            </div>
            <input
              type="url"
              id={`meeting-link-${courseId}`}
              value={linkInput}
              onChange={handleInputChange}
              placeholder="https://zoom.us/j/... o https://meet.google.com/..."
              disabled={isSubmitting}
              className={`
                w-full pl-11 pr-4 py-3 bg-white/5 border rounded-xl
                text-white placeholder-slate-500
                focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
                ${error 
                  ? 'border-red-500/50 focus:ring-red-500/50 focus:border-red-500' 
                  : 'border-white/10 hover:border-white/20'
                }
              `}
            />
          </div>
        </div>

        {/* Error Message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -10, height: 0 }}
              className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg"
            >
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Success Message */}
        <AnimatePresence>
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -10, height: 0 }}
              className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg"
            >
              <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <p className="text-emerald-400 text-sm">{success}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isSubmitting || !linkInput.trim()}
          whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
          whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
          className={`
            w-full py-3 rounded-xl font-semibold
            flex items-center justify-center gap-2
            transition-all duration-200
            ${isSubmitting || !linkInput.trim()
              ? 'bg-slate-600 cursor-not-allowed text-white/50'
              : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-lg shadow-blue-500/30 text-white'
            }
          `}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Actualizando...
            </>
          ) : (
            <>
              <Link2 className="w-4 h-4" />
              Actualizar Link
            </>
          )}
        </motion.button>
      </form>

      {/* History Section - Requirements: 2.6 */}
      <div className="border-t border-white/10 pt-4">
        <button
          onClick={toggleHistory}
          className="w-full flex items-center justify-between p-3 
                     bg-white/5 hover:bg-white/10 rounded-lg
                     border border-white/10 hover:border-white/20
                     transition-all duration-200"
        >
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-white/60" />
            <span className="text-sm text-white/80">Historial de enlaces</span>
          </div>
          {isHistoryOpen ? (
            <ChevronUp className="w-4 h-4 text-white/60" />
          ) : (
            <ChevronDown className="w-4 h-4 text-white/60" />
          )}
        </button>

        <AnimatePresence>
          {isHistoryOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-3 space-y-2">
                {/* Loading State */}
                {isLoadingHistory && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                  </div>
                )}

                {/* Error State */}
                {historyError && !isLoadingHistory && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-red-400 text-sm">{historyError}</p>
                  </div>
                )}

                {/* Empty State */}
                {!isLoadingHistory && !historyError && history.length === 0 && (
                  <div className="p-4 text-center">
                    <p className="text-white/50 text-sm">No hay historial de enlaces</p>
                  </div>
                )}

                {/* History List - Last 5 entries */}
                {!isLoadingHistory && !historyError && history.length > 0 && (
                  <div className="space-y-2">
                    {history.map((entry, index) => (
                      <motion.div
                        key={`${entry.updatedAt}-${index}`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="p-3 bg-white/5 rounded-lg border border-white/5"
                      >
                        <a
                          href={entry.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-cyan-400 hover:text-cyan-300 
                                     flex items-center gap-1 truncate mb-1"
                        >
                          <span className="truncate">{entry.meetingLink}</span>
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                        <div className="flex items-center justify-between text-xs text-white/40">
                          <span>{formatTimestamp(entry.updatedAt)}</span>
                          <span>{entry.updatedBy}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

MeetingLinkEditor.propTypes = {
  courseId: PropTypes.number.isRequired,
  courseName: PropTypes.string,
  currentLink: PropTypes.string,
  lastUpdatedAt: PropTypes.string,
  token: PropTypes.string.isRequired,
  onLinkUpdated: PropTypes.func,
  className: PropTypes.string,
};
